import React from "react";
import {
	Typography,
	Button,
	LinearProgress,
	Box,
	IconButton,
	Modal,
} from "@mui/material";
import { MapViewContext } from "../../contexts/MapViewContext";
import { AlignmentContext } from "../../contexts/AlignmentContext";
import { PrioritizeSlidersContext } from "../../contexts/PrioritizeSlidersContext";
import { execute } from "@arcgis/core/rest/print";
import PrintParameters from "@arcgis/core/rest/support/PrintParameters";
import PrintTemplate from "@arcgis/core/rest/support/PrintTemplate";
import { mapExportURL } from "../../constants";
import CloseIcon from "@mui/icons-material/Close";
import { trackEvent } from "../../hooks/useAnalytics";

// Style for the modal that appears when the download is ready
const modalStyle = {
	position: "absolute" as "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	width: 400,
	bgcolor: "background.paper",
	boxShadow: 24,
	p: 4,
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
};

export default function DownloadPdfBtn() {
	// State variable used to determine if the view is ready to be accessed by the print template
	const [viewReady, setViewReady] = React.useState<boolean | undefined>(false);
	// State variable used to determine whether the report is currently generating
	const [printing, setPrinting] = React.useState<boolean | undefined>(false);
	// State variable used to determine whether the report geoprocess is complete
	const [complete, setComplete] = React.useState<boolean>(false);
	// Variable used to store the url returned by the print service
	const [pdfUrl, setPdfUrl] = React.useState<string>("");
	const { alignment } = React.useContext(AlignmentContext);
	const view = React.useContext(MapViewContext);
	const prioContext = React.useContext(PrioritizeSlidersContext);
	const prioValues = prioContext.prioritizeSlidersValue;
	view?.when(() => {
		if (!viewReady) {
			setViewReady(true);
		}
	});

	const exportReport = async (params: any) => {
		const printTask = await execute(mapExportURL, params);
		setPdfUrl(printTask.url);
		setPrinting(false);
		setComplete(true);
		trackEvent("export_map_pdf", alignment);
	};

	const handleClick = (e: any) => {
		if (view) {
			try {
				//trackExportMapPDF();
				trackEvent("export_map", alignment);
				setPrinting(true);
				// The template named "Letter_Portrait_Normal" is the default template used
				const template = new PrintTemplate({
					format: "pdf",
					// @ts-ignore
					layout: "Letter_Portrait_Normal",
					layoutOptions: {
						titleText: "ParkServe Map Export",
					},
				});
				const prioritizeLayer = view.map.findLayerById("prioritize");
				// If the custom prioritize layer is added to the map and visible, the "Letter_Portrait_Prioritize" template is used
				if (prioritizeLayer && prioritizeLayer.visible) {
					// @ts-ignore
					template.layout = "Letter_Portrait_Prioritize";
					// The prioritize values are passed to the print template's layoutOptions using the customTextElements property
					template.layoutOptions.customTextElements = [
						{ LowInc: `${prioValues.LowIncPercRank}` },
						{ PeopleOfColor: `${prioValues.POCPercRank}` },
						{ HeatIslands: `${prioValues.HeatRank}` },
						{ AirPollution: `${prioValues.RespRank}` },
						{ PopulationDensity: `${prioValues.PPARank}` },
						{ PoorMentalHealth: `${prioValues.MentalRank}` },
						{ PhysicalActivity: `${prioValues.LPARank}` },
					];
				}
				const params = new PrintParameters({
					view: view,
					template: template,
				});
				exportReport(params);
			} catch (error) {
				console.log("Error occurred during printing", error);
			}
		}
	};

	return (
		<>
			<Button
				variant="contained"
				onClick={handleClick}
				disabled={!viewReady || printing}
			>
				{printing ? (
					<Box>
						<Typography variant="link">Exporting map...</Typography>
						<LinearProgress />
					</Box>
				) : (
					<Typography variant="link">Export Map PDF</Typography>
				)}
			</Button>
			<Modal
				open={complete}
				onClose={() => setComplete(false)}
				aria-labelledby="modal-modal-title"
				aria-describedby="modal-modal-description"
			>
				<Box sx={modalStyle}>
					<IconButton
						aria-label="close"
						onClick={() => setComplete(false)}
						sx={{
							position: "absolute",
							right: 8,
							top: 8,
							color: (theme) => theme.palette.grey[500],
						}}
					>
						<CloseIcon />
					</IconButton>
					<Typography
						id="modal-modal-title"
						variant="h3"
						component="h2"
					>
						PDF Map Generated
					</Typography>
					<Button
						variant="contained"
						sx={{ marginTop: "20px" }}
						href={pdfUrl}
						target="_blank"
					>
						Download PDF Map
					</Button>
				</Box>
			</Modal>
		</>
	);
}
